import React from "react";
import { BsLinkedin } from "react-icons/bs";
import { motion } from "framer-motion";
import Header from "../components/Header";
import { Link } from "../router";
import { LINKEDIN_URL, DOMESCRIBE_URL } from "../config";

const alsoIncluded = [
  {
    name: "Audio extraction",
    description: "Pull a clean audio track out of any video file.",
  },
  {
    name: "Video conversion",
    description: "Convert video between MP4, MOV, WebM, AVI, and MKV.",
  },
  {
    name: "Video compression",
    description:
      "Shrink video files by up to 70% with no noticeable drop in quality.",
  },
  {
    name: "Audio compression",
    description: "Reduce audio file sizes across the common formats.",
  },
];

const DomeScribe: React.FC = () => {
  return (
    <main className="min-h-screen bg-[#F9FAFB] dark:bg-gray-900 text-domichron-text dark:text-white font-sans antialiased relative text-sm sm:text-base md:text-lg">
      <Header />

      {/* Hero */}
      <section className="relative px-6 lg:px-20 pt-16 pb-20 bg-[#F9FAFB] dark:bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-domichron-orange transition"
          >
            ← Back to Domichron
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-6 space-y-6"
          >
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-domichron-orange">
              A Domichron venture
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold leading-tight text-domichron-text dark:text-white">
              DomeScribe. Accurate AI transcription.
            </h1>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl">
              Upload any audio or video and get a full, accurate transcript —
              in 99 languages. Text you can search, edit, and actually use,
              with your files kept private and encrypted.
            </p>
            <div className="pt-2">
              <a
                href={DOMESCRIBE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-block"
              >
                Open DomeScribe
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Transcription — the core */}
      <section className="section-padding bg-[#F1F5F9] dark:bg-gray-800">
        <div className="max-w-4xl mx-auto">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-domichron-orange mb-3">
            What it's built around
          </span>
          <h2 className="text-3xl font-bold mb-6 text-domichron-text dark:text-white">
            Transcription, done well
          </h2>
          <div className="space-y-5 text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
            <p>
              Drop in a recording — a podcast, an interview, a lecture, a
              meeting — and DomeScribe turns it into clean, accurate text.
              Transcription across 99 languages is the core of the product, and
              everything else is built around it.
            </p>
            <p>
              Your transcript is something you can work with: search it for the
              moment you need, edit it, and reuse it. Files are handled
              privately and encrypted, with security controls for teams that
              need them.
            </p>
          </div>
        </div>
      </section>

      {/* Also included */}
      <section className="section-padding bg-[#F9FAFB] dark:bg-gray-900">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold mb-8 text-domichron-text dark:text-white">
            Plus the media tools around it
          </h2>
          <div className="grid gap-6 md:grid-cols-2">
            {alsoIncluded.map((tool) => (
              <div
                key={tool.name}
                className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm"
              >
                <h3 className="text-lg font-bold text-domichron-text dark:text-white">
                  {tool.name}
                </h3>
                <p className="text-base text-gray-700 dark:text-gray-300 mt-2 leading-relaxed">
                  {tool.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white dark:bg-gray-800 text-center">
        <div className="max-w-2xl mx-auto space-y-6">
          <h2 className="text-3xl font-bold text-domichron-text dark:text-white">
            Try DomeScribe
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300">
            It's free to get started. Upload a file and see what it does.
          </p>
          <a
            href={DOMESCRIBE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-block"
          >
            Open DomeScribe
          </a>
        </div>
      </section>

      <footer className="py-8 border-t border-gray-200 dark:border-gray-800">
        <div className="flex flex-col items-center gap-3 px-6">
          <a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-base text-gray-700 dark:text-gray-300 hover:text-domichron-orange transition-colors"
          >
            <BsLinkedin className="w-5 h-5" />
            <span>Connect on LinkedIn</span>
          </a>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            © {new Date().getFullYear()} Domichron, Inc. All rights reserved.
          </p>
        </div>
      </footer>
    </main>
  );
};

export default DomeScribe;
